<script setup lang="ts" generic="T extends Record<string, any> = Record<string, any>">
import { useControlId } from '../../composables/useControlId'
import { computed, ref, shallowRef, toRef, type VNodeChild } from 'vue'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from '@lucide/vue'
import RrEmpty from '../empty/RrEmpty.vue'
import RrSkeleton from '../skeleton/RrSkeleton.vue'
import RrPagination from '../pagination/RrPagination.vue'
import RenderContent from '../internal/RenderContent'
import TableColumnFilter from './TableColumnFilter.vue'
import { useUiConfig } from '../config-provider/context'
import { cellValue, columnKey, useTableData } from './useTableData'
import { useRowSelection } from './useRowSelection'
import { useFixedColumns } from './useFixedColumns'
import type { TableChangeExtra, TableColumnType, TableFilters, TableKey, TablePaginationConfig, TableRowSelection, TableSize, TableSorter } from './types'

const props = withDefaults(defineProps<{
  columns?: TableColumnType<T>[]
  dataSource?: T[]
  rowKey?: string | ((record: T) => TableKey)
  childrenColumnName?: string
  loading?: boolean
  size?: TableSize
  stripe?: boolean
  rowSelection?: TableRowSelection<T>
  scroll?: { x?: number | string; y?: number | string }
  pagination?: false | TablePaginationConfig
  expandedRowKeys?: TableKey[]
  defaultExpandedRowKeys?: TableKey[]
  defaultExpandAllRows?: boolean
  emptyText?: VNodeChild
  emptyDescription?: string
  minWidth?: number | string
}>(), { columns: () => [], dataSource: () => [], rowKey: 'key', childrenColumnName: 'children', loading: false, stripe: false, pagination: () => ({}), defaultExpandedRowKeys: () => [], defaultExpandAllRows: false, emptyDescription: '' })
const emit = defineEmits<{
  'row-click': [record: T, index: number, event: MouseEvent]
  'update:selectedRowKeys': [keys: TableKey[]]
  'update:expandedRowKeys': [keys: TableKey[]]
  expand: [expanded: boolean, record: T]
  expandedRowsChange: [keys: TableKey[]]
  change: [pagination: TablePaginationConfig, filters: TableFilters, sorter: TableSorter<T>, extra: TableChangeExtra<T>]
}>()
const config = useUiConfig()
const tableRef = ref<HTMLTableElement | null>(null)
const bodyRef = ref<HTMLTableSectionElement | null>(null)
const records = computed(() => props.dataSource)
const visibleColumns = computed(() => props.columns.filter(column => column.visible !== false))
const size = computed(() => props.size ?? config.value.componentSize ?? 'large')
const { recordKey, childrenOf, recordIndex, pageRecords, processedRecords, pagination, filters, sorter, changeSort, changeFilter, changePage } = useTableData(props, (...args) => emit('change', ...args))
const innerExpandedKeys = shallowRef(new Set(props.defaultExpandAllRows ? [...recordIndex.value.keys()] : props.defaultExpandedRowKeys))
const expandedKeys = computed(() => props.expandedRowKeys ? new Set(props.expandedRowKeys) : innerExpandedKeys.value)
function toggleExpandRow(record: T) {
  const key = recordKey(record)
  const next = new Set(expandedKeys.value)
  const expanded = !next.has(key)
  if (expanded) next.add(key)
  else next.delete(key)
  innerExpandedKeys.value = next
  emit('update:expandedRowKeys', [...next])
  emit('expand', expanded, record)
  emit('expandedRowsChange', [...next])
}
const displayRows = computed(() => {
  const rows: { record: T; index: number; key: TableKey; level: number; hasChildren: boolean; isExpanded: boolean }[] = []
  function visit(items: T[], level: number) {
    for (const record of items) {
      const key = recordKey(record)
      const children = childrenOf(record)
      const expanded = expandedKeys.value.has(key)
      rows.push({ record, index: rows.length, key, level, hasChildren: children.length > 0, isExpanded: expanded })
      if (expanded) visit(children, level + 1)
    }
  }
  visit(pageRecords.value, 0)
  return rows
})
const pageIndex = computed(() => {
  const index = new Map<TableKey, T>()
  function visit(items: T[]) { for (const item of items) { const key = recordKey(item); index.set(key, recordIndex.value.get(key)!); visit(childrenOf(item)) } }
  visit(pageRecords.value)
  return index
})
const { selectedKeys, selectableRecords, isAllSelected, isIndeterminate, checkboxProps, toggleRow, toggleAll } = useRowSelection(toRef(props, 'rowSelection'), recordIndex, pageIndex, recordKey, keys => emit('update:selectedRowKeys', keys))
const { colStyle, selectionStyle } = useFixedColumns(visibleColumns, toRef(props, 'rowSelection'), tableRef)
const selectionName = `rr-table-selection-${useControlId()}`
const paginationProps = computed(() => { const { onChange: _change, onShowSizeChange: _size, ...options } = pagination.value; return options })
const emptyContent = computed(() => props.emptyText ?? config.value.renderEmpty?.('Table'))
function sortOrder(column: TableColumnType<T>, index: number) { return sorter.value.columnKey === (column.key ?? columnKey(column, index)) ? sorter.value.order : null }
function renderCell(record: T, column: TableColumnType<T>, index: number): VNodeChild {
  const text = cellValue(record, column)
  return column.customRender ? column.customRender({ text, record, index, column }) : text == null ? '' : String(text)
}
defineExpose({ body: bodyRef, nativeElement: tableRef })
</script>

<template>
  <div class="rr-table-wrapper" :class="[`is-size-${size}`, { 'is-striped': stripe }]" :aria-busy="loading">
    <div class="rr-table-container" :style="{ maxHeight: typeof scroll?.y === 'number' ? `${scroll.y}px` : scroll?.y }">
      <table ref="tableRef" class="rr-table" :style="{ minWidth: typeof (minWidth ?? scroll?.x) === 'number' ? `${minWidth ?? scroll?.x}px` : minWidth ?? scroll?.x }">
        <colgroup><col v-if="rowSelection" :style="selectionStyle" /><col v-for="(column, index) in visibleColumns" :key="columnKey(column, index)" :style="colStyle(column, index)" /></colgroup>
        <thead><tr>
          <th v-if="rowSelection" class="rr-table-cell--selection" data-column-key="__selection" :style="selectionStyle">
            <input v-if="rowSelection.type !== 'radio'" type="checkbox" :checked="isAllSelected" :indeterminate="isIndeterminate" :disabled="loading || !selectableRecords.length" aria-label="全选" @change="toggleAll" />
          </th>
          <th v-for="(column, index) in visibleColumns" :key="columnKey(column, index)" :data-column-key="columnKey(column, index)" class="rr-table-cell" :class="[`text-${column.align ?? 'left'}`, { 'is-sortable': column.sorter, 'is-fixed-left': column.fixed === true || column.fixed === 'left', 'is-fixed-right': column.fixed === 'right' }]" :style="colStyle(column, index)" :aria-sort="column.sorter ? sortOrder(column, index) === 'ascend' ? 'ascending' : sortOrder(column, index) === 'descend' ? 'descending' : 'none' : undefined" :tabindex="column.sorter ? 0 : undefined" @click="changeSort(column)" @keydown.enter.prevent="changeSort(column)" @keydown.space.prevent="changeSort(column)">
            <div class="rr-table-cell__header-content">
              <slot name="headerCell" :column="column" :title="column.title"><RenderContent :value="column.title" /></slot>
              <span v-if="column.sorter" class="rr-table-sorter" aria-hidden="true"><ArrowUp v-if="sortOrder(column, index) === 'ascend'" :size="13" class="is-active" /><ArrowDown v-else-if="sortOrder(column, index) === 'descend'" :size="13" class="is-active" /><ArrowUpDown v-else :size="13" class="is-neutral" /></span>
              <TableColumnFilter v-if="column.filters" :options="column.filters" :multiple="column.filterMultiple" :value="filters[columnKey(column, index)]" :title="typeof column.title === 'string' ? column.title : undefined" :disabled="loading" @change="changeFilter(column, $event)" />
            </div>
          </th>
        </tr></thead>
        <tbody ref="bodyRef">
          <tr v-if="loading && !records.length" class="rr-table-placeholder-row"><td :colspan="visibleColumns.length + (rowSelection ? 1 : 0)" class="rr-table-cell--skeleton"><RrSkeleton variant="table" :rows="5" /></td></tr>
          <tr v-else-if="!displayRows.length" class="rr-table-placeholder-row"><td :colspan="visibleColumns.length + (rowSelection ? 1 : 0)" class="rr-table-cell--empty"><slot name="emptyText"><RenderContent v-if="emptyContent !== undefined" :value="emptyContent" /><RrEmpty v-else :description="emptyDescription" /></slot></td></tr>
          <tr v-for="row in displayRows" v-else :key="row.key" :data-id="row.key" class="rr-table-row" :class="{ 'is-selected': selectedKeys.has(row.key) }" @click="emit('row-click', row.record, row.index, $event)">
            <td v-if="rowSelection" class="rr-table-cell--selection" :style="selectionStyle" @click.stop><input v-bind="checkboxProps(row.record)" :type="rowSelection.type === 'radio' ? 'radio' : 'checkbox'" :name="rowSelection.type === 'radio' ? selectionName : checkboxProps(row.record).name" :checked="selectedKeys.has(row.key)" :disabled="loading || checkboxProps(row.record).disabled" :aria-label="checkboxProps(row.record)['aria-label'] ?? `选择行 ${row.key}`" @change="toggleRow(row.record, $event)" /></td>
            <td v-for="(column, index) in visibleColumns" :key="columnKey(column, index)" class="rr-table-cell" :class="[`text-${column.align ?? 'left'}`, { 'is-ellipsis': column.ellipsis, 'is-fixed-left': column.fixed === true || column.fixed === 'left', 'is-fixed-right': column.fixed === 'right' }]" :style="colStyle(column, index)">
              <div class="rr-table-cell__content" :style="index === 0 && row.level ? { paddingLeft: `${row.level * 18}px` } : undefined">
                <button v-if="index === 0 && row.hasChildren" type="button" class="rr-table-expand-icon" :class="{ 'is-expanded': row.isExpanded }" :aria-expanded="row.isExpanded" aria-label="展开/折叠子行" @click.stop="toggleExpandRow(row.record)"><ChevronRight :size="11" aria-hidden="true" /></button>
                <span v-else-if="index === 0 && pageRecords.some(record => childrenOf(record).length)" class="rr-table-expand-spacer" />
                <slot name="bodyCell" :column="column" :record="row.record" :index="row.index" :text="cellValue(row.record, column)"><RenderContent :value="renderCell(row.record, column, row.index)" /></slot>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="$slots.summary"><slot name="summary" :currentData="processedRecords" /></tfoot>
      </table>
    </div>
    <RrPagination v-if="props.pagination !== false" v-bind="paginationProps" :page-size-options="pagination.pageSizeOptions?.map(Number)" :show-total="pagination.showTotal" :loading="loading" @change="changePage" />
  </div>
</template>

<style scoped>
.rr-table-wrapper {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.rr-table-container {
  overflow: auto;
  width: 100%;
}

.rr-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-md);
  text-align: left;
}

.rr-table-cell {
  position: relative;
  box-sizing: border-box;
  padding: var(--rr-space-4);
  border-bottom: 1px solid var(--rr-color-border);
  vertical-align: middle;
  transition: background 120ms ease, padding 120ms ease;
}

.rr-table-cell.text-left { text-align: left; }
.rr-table-cell.text-center { text-align: center; }
.rr-table-cell.text-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rr-table-cell.is-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Header Cells */
thead th.rr-table-cell {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: var(--rr-font-size-md);
  font-weight: var(--rr-font-weight-bold);
  white-space: nowrap;
  user-select: none;
}

.rr-table-cell__header-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rr-table-cell.is-sortable {
  cursor: pointer;
}
.rr-table-cell.is-sortable:hover {
  background: var(--rr-color-border);
}

.rr-table-sorter {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.rr-table-sorter .is-active {
  color: var(--rr-color-primary);
}
.rr-table-sorter .is-neutral {
  color: var(--rr-color-text-subtle);
}

/* Selection Cells */
.rr-table-cell--selection {
  width: 48px;
  min-width: 48px;
  text-align: center;
  padding: var(--rr-space-4) var(--rr-space-2);
  border-bottom: 1px solid var(--rr-color-border);
  vertical-align: middle;
}

.rr-table-cell--selection input {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
  max-width: 16px !important;
  max-height: 16px !important;
  margin: 0 !important;
  padding: 0 !important;
  cursor: pointer;
  vertical-align: middle;
}

thead th.rr-table-cell--selection {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--rr-color-surface-subtle);
}

/* Fixed Columns */
.rr-table-cell.is-fixed-left {
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
}
thead th.rr-table-cell.is-fixed-left {
  z-index: 3;
  background: var(--rr-color-surface-subtle);
}

.rr-table-cell.is-fixed-right {
  position: sticky;
  right: 0;
  z-index: 1;
  background: inherit;
  box-shadow: -6px 0 10px -6px rgba(0, 0, 0, 0.12);
}
thead th.rr-table-cell.is-fixed-right {
  z-index: 3;
  background: var(--rr-color-surface-subtle);
}

/* Row Hover & Selection */
.rr-table-row {
  background: var(--rr-color-surface);
  transition: background-color 120ms ease;
}

.rr-table-row:hover {
  background: var(--rr-color-surface-subtle);
}

.rr-table-row.is-selected {
  background: var(--rr-color-primary-soft);
}

.is-striped .rr-table-row:nth-child(even):not(:hover):not(.is-selected) {
  background: var(--rr-color-surface-subtle);
}

/* Densities */
.is-size-small .rr-table-cell,
.is-size-small .rr-table-cell--selection {
  padding: var(--rr-space-2);
}

.is-size-middle .rr-table-cell,
.is-size-middle .rr-table-cell--selection {
  padding: var(--rr-space-3) var(--rr-space-2);
}

.rr-table-cell--skeleton {
  padding: 16px;
  border-bottom: 0;
}

.rr-table-cell--empty {
  padding: 32px 16px;
  text-align: center;
  border-bottom: 0;
}

.rr-table-cell__content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.rr-table-expand-icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 16px !important;
  height: 16px !important;
  min-height: unset !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 1px solid var(--rr-color-border-strong, #cbd2da) !important;
  border-radius: var(--rr-radius-xs, 4px) !important;
  background: var(--rr-color-surface, #ffffff) !important;
  color: var(--rr-color-text-muted, #67717f) !important;
  cursor: pointer;
  transition: transform 150ms ease, color 150ms ease;
  flex-shrink: 0;
}

.rr-table-expand-icon:hover {
  color: var(--rr-color-primary, #5a46e0) !important;
  border-color: var(--rr-color-primary, #5a46e0) !important;
}

.rr-table-expand-icon.is-expanded {
  transform: rotate(90deg);
}

.rr-table-expand-spacer {
  display: inline-block;
  width: 16px;
  flex-shrink: 0;
}
</style>
